import { ReactNode } from 'react'

import {
  PerformanceReviewOverviewPageCompaniesLegalEntityFragment,
  PerformanceReviewOverviewPageLocationsFragment,
  PerformanceReviewOverviewPageTeamsFragment,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { AgreementStatusFilter } from './AgreementStatusFilter'
import { EmployeeFilter } from './EmployeeFilter'
import { EvaluationsStatusFilter } from './EvaluationsStatusFilter'
import { LegalEntityFilter } from './LegalEntityFilter'
import { ManagerFilter } from './ManagerFilter'
import { OnlyDirectReportsFilter } from './OnlyDirectReportsFilter'
import { ReviewerStrategyFilter } from './ReviewerStrategyFilter'
import { ScoreFilter, ScoreScaleItem } from './ScoreFilter'
import { TeamsFilter } from './TeamsFilter'
import { TenureFilter } from './TenureFilter'
import { WorkplaceFilter } from './WorkplaceFilter'
import { SupportedFilters } from './types'

export interface FilterConfig {
  key: keyof SupportedFilters
  label: string
  component: () => ReactNode
}

export interface FilterConfigProps {
  materializedTargetIds?: string[]
  teams?: PerformanceReviewOverviewPageTeamsFragment[]
  locations?: PerformanceReviewOverviewPageLocationsFragment[]
  legalEntities?: PerformanceReviewOverviewPageCompaniesLegalEntityFragment[]
  scoreScale?: ScoreScaleItem[]
  hasEmployeeScores?: boolean
  excludeAgreementStatus?: boolean
}

export const createFilterConfig = (props: FilterConfigProps): Record<string, FilterConfig> => {
  const baseConfig: Record<string, FilterConfig> = {
    managerId: {
      key: 'managerId',
      label: i18n.t('common.manager'),
      component: () => <ManagerFilter key="managerId" />,
    },
    onlyDirectReports: {
      key: 'onlyDirectReports',
      label: i18n.t(
        'performance_process.reviews.target_datatable.filters.only_direct_reports.title'
      ),
      component: () => <OnlyDirectReportsFilter key="onlyDirectReports" />,
    },
    employeeIds: {
      key: 'employeeIds',
      label: i18n.t('common.employee'),
      component: () => (
        <EmployeeFilter key="employeeIds" materializedTargetIds={props?.materializedTargetIds} />
      ),
    },
    teamIds: {
      key: 'teamIds',
      label: i18n.t('performance_process.reviews.filters.teams'),
      component: () => <TeamsFilter key="teamIds" teams={props?.teams ?? []} />,
    },
    evaluationsStatus: {
      key: 'evaluationsStatus',
      label: i18n.t('performance_process.reviews.target_datatable.results.filter.title'),
      component: () => <EvaluationsStatusFilter key="evaluationsStatus" />,
    },
    scores: {
      key: 'scores',
      label: i18n.t('performance_process.reviews.target_datatable.final_score.title'),
      component: () => {
        if (props?.hasEmployeeScores) {
          return <ScoreFilter key="scores" scoreScale={props?.scoreScale ?? []} />
        }
        return null
      },
    },
    tenureDates: {
      key: 'tenureDates',
      label: i18n.t('performance_process.reviews.filters.tenure'),
      component: () => <TenureFilter key="tenureDates" />,
    },
    workplaceIds: {
      key: 'workplaceIds',
      label: i18n.t('performance_process.reviews.filters.workplace'),
      component: () => <WorkplaceFilter key="workplaceIds" locations={props?.locations ?? []} />,
    },
    legalEntityIds: {
      key: 'legalEntityIds',
      label: i18n.t('performance_process.reviews.filters.legal_entity'),
      component: () => (
        <LegalEntityFilter key="legalEntityIds" legalEntities={props?.legalEntities ?? []} />
      ),
    },
    reviewerStrategy: {
      key: 'reviewerStrategy',
      label: i18n.t('performance_process.insights.answers.filters.reviewer_strategy.title'),
      component: () => <ReviewerStrategyFilter key="reviewerStrategy" />,
    },
  }

  if (!props?.excludeAgreementStatus) {
    baseConfig.agreementStatus = {
      key: 'agreementStatus',
      label: i18n.t('performance_process.agreements.filters.status.title'),
      component: () => <AgreementStatusFilter key="agreementStatus" />,
    }
  }

  return baseConfig
}

export const FILTER_CONFIGS: Record<
  'insights' | 'answers' | 'heatmap' | 'teamInsights',
  {
    persistentFilterKeys: Array<keyof SupportedFilters>
    conditionalFilterKeys: Array<keyof SupportedFilters>
  }
> = {
  insights: {
    persistentFilterKeys: [
      'managerId',
      'onlyDirectReports',
      'employeeIds',
      'teamIds',
      'evaluationsStatus',
      'scores',
    ],
    conditionalFilterKeys: ['tenureDates', 'workplaceIds', 'legalEntityIds', 'agreementStatus'],
  },
  answers: {
    persistentFilterKeys: [
      'managerId',
      'onlyDirectReports',
      'employeeIds',
      'reviewerStrategy',
      'teamIds',
    ],
    conditionalFilterKeys: ['tenureDates', 'workplaceIds', 'legalEntityIds'],
  },
  heatmap: {
    persistentFilterKeys: [
      'managerId',
      'onlyDirectReports',
      'employeeIds',
      'reviewerStrategy',
      'teamIds',
    ],
    conditionalFilterKeys: ['tenureDates', 'workplaceIds', 'legalEntityIds'],
  },
  teamInsights: {
    persistentFilterKeys: ['managerId'],
    conditionalFilterKeys: [],
  },
} as const

export type FilterConfigType = keyof typeof FILTER_CONFIGS
